import { useState } from "react"
import { createPortal } from "react-dom"
import parse from "html-react-parser"

import Modal from "shared/components/modal"
import SuccessModal from "app/components/billing/success_modal"
import { updateSubscriptionPlan } from "app/components/upgrade_plan_modal/pro_upgrade/api"

import iftttLogo from "images/logo-ifttt.svg?raw"
import proBadge from "images/plan-badges/pro.svg?raw"

import styles from "app/components/upgrade_plan_modal/shared_modal.module.scss"

const PRO_FEATURES = [
  "20 Applets",
  "Fastest Applet speeds",
  "Multi-action Applets",
  "Exclusive triggers and actions",
  "Customer support",
]

interface Props {
  intermediateProProductId: string
  subscriptionPaymentType: string
}

const PlanDowngradeButton = ({ intermediateProProductId, subscriptionPaymentType }: Props) => {
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  if (subscriptionPaymentType !== "stripe") return null

  const handleClick = (evt: MouseEvent) => {
    evt.preventDefault()
    setShowModal(true)
  }

  const handleClose = () => {
    setShowModal(false)
  }

  const handleDowngrade = async () => {
    setLoading(true)

    try {
      const { errors } = await updateSubscriptionPlan({ productId: intermediateProProductId })

      if (!errors?.length) {
        setShowModal(false)
        setShowSuccess(true)
      } else {
        const errorMessage = errors[0]?.message || "Something went wrong. Please try again."
        window.appendFlash(errorMessage, "danger")
        setShowModal(false)
      }
    } catch {
      window.appendFlash("Something went wrong. Please try again.", "danger")
      setShowModal(false)
    } finally {
      setLoading(false)
    }
  }

  if (showSuccess) {
    return createPortal(
      <SuccessModal
        title="You've switched to"
        badge={iftttLogo + proBadge}
        featuresLabel="Pro plan includes:"
        features={PRO_FEATURES}
        visible={true}
        onClose={() => {
          window.location.href = "/billing"
        }}
        buttons={[
          {
            label: "Back to Billing",
            onClick: () => {
              window.location.href = "/billing"
            },
            variant: "secondary",
          },
          {
            label: "Explore Applets",
            onClick: () => {
              window.location.href = "/explore"
            },
            variant: "primary",
          },
        ]}
      />,
      document.body
    )
  }

  return (
    <>
      <button className="button-secondary button-on-color" onClick={handleClick}>
        Switch to Pro
      </button>
      {showModal && createPortal(
        <div className={styles["modal-container"]}>
          <Modal show={showModal} closeListener={handleClose} includeCloseX={true}>
            <h2 className="mt0 mb0">Switch to</h2>
            <div className="success-modal-badge">{parse(iftttLogo + proBadge)}</div>
            <p>
              You&apos;ll lose access to unlimited Applets, queries, filter code, AI services, and multiple accounts per
              service. You&apos;ll keep 20 Applets, multi-action Applets, Webhooks, and customer support.
            </p>
            <div className="action-buttons-row">
              <button className="button-tertiary button-outlined" onClick={handleClose}>
                Nevermind
              </button>
              <button className="button-tertiary" disabled={loading} onClick={handleDowngrade}>
                {loading ? "Switching..." : "Switch to Pro"}
              </button>
            </div>
          </Modal>
        </div>,
        document.body
      )}
    </>
  )
}

export default PlanDowngradeButton
